import { faGear } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dialog, DialogContent, DialogTitle, Icon } from '@mui/material'
import { PaperArrastavel } from 'app/components/dialog-arrastavel/PaperArrastavel'
import clsx from 'clsx'
import React, { useState, useCallback, useMemo } from 'react'

const estilosGridItens = require('./grid-estoque.module.scss').default
const estilosTableHead = require('../../itens-orcamento/grid-itens/head/tableHead.module.scss').default

import { IColuna } from 'app/types/IColuna'

export type AcoesHeadEstoqueProps = {
    onAlterarFonte?: (tamanho: number) => void
    colunas: IColuna[]
    onToggleColunaSelecionada: (nomeColuna: string, selecionada: boolean) => void
    onToggleSelecionarTodasColunas: (selecionar: boolean) => void
    tamanhoFonte: number
    exibirIconesAumentarDiminuirFonte?: boolean
    larguraLinha?: number
    nomeGrid?: "PESQUISAR_CLIENTE_ORCAMENTO"
    permiteEngrenagemGrid: boolean
}

const normalizarNomeColuna = (nomeColuna: string) => (nomeColuna ?? "").replace(/\s+/g, '_')

export const AcoesHeadEstoque = React.memo(({
    onAlterarFonte,
    colunas,
    onToggleColunaSelecionada,
    onToggleSelecionarTodasColunas,
    tamanhoFonte,
    exibirIconesAumentarDiminuirFonte = true,
    larguraLinha,
    permiteEngrenagemGrid
}: AcoesHeadEstoqueProps) => {
    const [mostrarFerramentas, setMostrarFerramentas] = useState<boolean>(false)
    const [icon, setIcon] = useState<string>('arrow_drop_up')
    const [exibirSelecaoColunas, setExibirSelecaoDeColunas] = useState(false)

    const handleToggleFerramentas = useCallback(() => {
        setMostrarFerramentas(prev => !prev)
        setIcon(prev => prev === 'arrow_drop_up' ? 'arrow_drop_down' : 'arrow_drop_up')
    }, [])

    const handleFecharSelecaoColunas = useCallback(() => setExibirSelecaoDeColunas(false), [])
    const handleAbrirSelecaoColunas = useCallback(() => setExibirSelecaoDeColunas(true), [])

    const handleToggleSelecionarTodas = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => onToggleSelecionarTodasColunas(e.target.checked),
        [onToggleSelecionarTodasColunas]
    )

    const handleAumentarFonte = useCallback(
        () => onAlterarFonte?.(tamanhoFonte + 1),
        [onAlterarFonte, tamanhoFonte]
    )

    const handleDiminuirFonte = useCallback(
        () => onAlterarFonte?.(tamanhoFonte - 1),
        [onAlterarFonte, tamanhoFonte]
    )

    const todasColunasSelecionadas = useMemo(() => colunas.every(c => c.Selecionada), [colunas])

    const selecaoDeColunas = useMemo(() => (
        <div className="_grid _grid-cols-1 _grid-flow-row" id="AcoesHeadEstoque_SelecaoDeColunas">
            <div className="_flex _flex-row _justify-start _max-w-xs _items-end" id="AcoesHeadEstoque_LinhaTodasColunas">
                <div className="_mr-4">
                    <input
                        type="checkbox"
                        id="AcoesHeadEstoque_CheckboxTodasColunas"
                        checked={todasColunasSelecionadas}
                        onChange={handleToggleSelecionarTodas}
                    />
                </div>
                <b>Todas</b>
            </div>
            {colunas.map((coluna, index) => {
                const nomeColunaNormalizado = normalizarNomeColuna(coluna.Nome)
                return (
                    <div
                        className="_flex _flex-row _justify-start _max-w-xs _items-end"
                        key={coluna.Nome}
                        id={`AcoesHeadEstoque_SelecaoColuna_${nomeColunaNormalizado}_${index}`}
                    >
                        <div className="_mr-4">
                            <input
                                type="checkbox"
                                id={`AcoesHeadEstoque_CheckboxColuna_${nomeColunaNormalizado}_${index}`}
                                checked={coluna.Selecionada ?? false}
                                onChange={() => onToggleColunaSelecionada(coluna.Nome, !coluna.Selecionada)}
                            />
                        </div>
                        {coluna.Nome}
                    </div>
                )
            })}
        </div>
    ), [colunas, todasColunasSelecionadas, handleToggleSelecionarTodas, onToggleColunaSelecionada])

    const selecaoDeColunasDialog = useMemo(() => (
        <Dialog open={exibirSelecaoColunas} onClose={handleFecharSelecaoColunas} id="AcoesHeadEstoque_DialogSelecaoColunas" PaperComponent={PaperArrastavel}>
            <DialogTitle>Selecionar colunas para exibição</DialogTitle>
            <DialogContent>{selecaoDeColunas}</DialogContent>
        </Dialog>
    ), [exibirSelecaoColunas, handleFecharSelecaoColunas, selecaoDeColunas])

    const botoes = useMemo(() => (
        <div
            style={{ width: larguraLinha ? `${larguraLinha}px` : undefined }}
            className={clsx(["_flex _flex-row _mt-4 _gap-x-2", estilosTableHead.acoesHead])}
            id="AcoesHeadEstoque_Botoes"
        >
            {permiteEngrenagemGrid &&
                <FontAwesomeIcon
                    icon={faGear}
                    className="_text-2xl _cursor-pointer"
                    id="AcoesHeadEstoque_IconeEngrenagem"
                    onClick={handleAbrirSelecaoColunas}
                />}
            {exibirIconesAumentarDiminuirFonte && (
                <>
                    <button
                        id="AcoesHeadEstoque_BotaoAumentarFonte"
                        className={clsx('no-global-button', estilosGridItens.botaoAumentarDiminuirFonte)}
                        onClick={handleAumentarFonte}
                        disabled={tamanhoFonte >= 26}
                    >
                        A+
                    </button>
                    <button
                        id="AcoesHeadEstoque_BotaoDiminuirFonte"
                        className={clsx('no-global-button', estilosGridItens.botaoAumentarDiminuirFonte)}
                        onClick={handleDiminuirFonte}
                        disabled={tamanhoFonte <= 8}
                    >
                        A-
                    </button>
                </>
            )}
        </div>
    ), [larguraLinha, permiteEngrenagemGrid, handleAbrirSelecaoColunas, exibirIconesAumentarDiminuirFonte, handleAumentarFonte, tamanhoFonte, handleDiminuirFonte])

    return (
        <>
            <div className={clsx("_mt-12", estilosTableHead.menu)} id="AcoesHeadEstoque_Menu">
                <button
                    style={{ marginLeft: '14px', height: '20px' }}
                    id="table-headgrid-itensarrow"
                    className="no-global-button"
                    onClick={handleToggleFerramentas}
                >
                    <Icon>{icon}</Icon>
                </button>
                {mostrarFerramentas && <section>{botoes}</section>}
            </div>
            {exibirSelecaoColunas && selecaoDeColunasDialog}
        </>
    )
})
